'use client';

import Link from 'next/link';
import { toTimeAgo } from '@/lib/dateTime';

import Tag from '@/ui/Tag';
import { IRepository } from '@/types';
import { IEvaluation } from '@/lib/evaluationsApi';
import { deleteEvaluationItem } from '../deleteEvaluationAction';
import * as Styled from './styled';
import { UserAvatar } from '@/components/Avatar';
import * as EvaluationTag from '@/components/Repository/Evaluations/EvaluationTag';
import CopyTextControl from '@/ui/CopyTextControl';
import SourceTargetFiles from './SourceTargetFiles';
import { sectionNavigationPath } from '@/app/(repository)/[namespace]/[repositoryName]/evaluations/consts';
import { Row } from '@/ui/primatives';

const StatusStack: React.FC<{ evaluation: IEvaluation; repository: IRepository }> = ({ evaluation, repository }) => {
  return (
    <Styled.StatusStack>
      <EvaluationTag.Status evaluation={evaluation} />
      <EvaluationTag.RunTime evaluation={evaluation} noBackground />
      {/* <EvaluationTag.Type evaluation={evaluation} noBackground /> */}
      <EvaluationTag.Rows evaluation={evaluation} noBackground />
      <EvaluationTag.Tokens evaluation={evaluation} noBackground />
      <EvaluationTag.Cost evaluation={evaluation} noBackground />
      <EvaluationTag.Iterations evaluation={evaluation} repository={repository} noBackground />
    </Styled.StatusStack>
  );
};

const EvaluationHeader: React.FC<{ evaluation: IEvaluation; repository: IRepository }> = ({
  evaluation,
  repository,
}) => {
  return (
    <Styled.EvaluationHeader>
      <Styled.EvaluationHeaderRow>
        <EvaluationTitle evaluation={evaluation} repository={repository} />
        <EvaluationId evaluation={evaluation} repository={repository} style={{ justifyContent: 'flex-end' }} />
      </Styled.EvaluationHeaderRow>
      <Styled.EvaluationHeaderRow>
        <Row alignItems="center" gap={5}>
          <EvaluationTag.ModelDeveloper
            logoSize={20}
            evaluation={evaluation}
            noBackground
            style={{ fontWeight: 600, fontSize: '14px' }}
          />
          <EvaluationTag.Type evaluation={evaluation} pill />
        </Row>
        <EvaluationCreated evaluation={evaluation} />
      </Styled.EvaluationHeaderRow>
    </Styled.EvaluationHeader>
  );
};

const EvaluationCreated: React.FC<{ evaluation: IEvaluation }> = ({ evaluation }) => {
  return (
    <Row alignItems="center" justifyContent="flex-end" gap={5}>
      <UserAvatar user={evaluation.created_by} size={20} />
      <div>{evaluation.created_by.username}</div>
      <small className="dim no-wrap">{toTimeAgo(evaluation.inserted_at)}</small>
    </Row>
  );
};

const EvaluationTitle: React.FC<{ evaluation: IEvaluation; repository: IRepository }> = ({
  evaluation,
  repository,
}) => {
  return (
    <Styled.EvaluationTitle>
      <Link
        className="link-underline"
        href={`/${repository.namespace}/${repository.name}/${sectionNavigationPath}/${evaluation.id}`}
      >
        {evaluation.name || evaluation.id}
      </Link>
    </Styled.EvaluationTitle>
  );
};

const EvaluationId: React.FC<{ evaluation: IEvaluation; repository: IRepository; style?: React.CSSProperties }> = ({
  evaluation,
  repository,
  style,
}) => {
  return (
    <Row alignItems="center" gap={5} style={style}>
      <Tag small variant="info" className="mono truncate">
        {evaluation.id}
      </Tag>
      <CopyTextControl
        id="evaluation-id"
        assetType="evaluation ID"
        value={evaluation.id}
        event={{
          name: 'copied_evaluation_id',
          payload: {
            repository_namespace: repository.namespace,
            repository_name: repository.name,
            evaluation_id: evaluation.id,
          },
        }}
      />
    </Row>
  );
};

const PromptSection: React.FC<{ evaluation: IEvaluation; repository: IRepository }> = ({ evaluation, repository }) => {
  return (
    <Styled.PromptSection>
      <Styled.Prompt $lines={4}>
        <label>
          <b>Prompt</b>
          <CopyTextControl
            id="evaluation-prompt"
            assetType="prompt"
            value={evaluation.prompt}
            event={{
              name: 'copied_evaluation_prompt',
              payload: {
                repository_namespace: repository.namespace,
                repository_name: repository.name,
                evaluation_id: evaluation.id,
              },
            }}
          />
        </label>
        <pre>{evaluation.prompt}</pre>
      </Styled.Prompt>
    </Styled.PromptSection>
  );
};

const EvaluationCard: React.FC<{ repository: IRepository; evaluation: IEvaluation; sectionNavigationPath: string }> = ({
  repository,
  evaluation,
  sectionNavigationPath,
}) => {
  const onDelete = (evaluationId: string) => {
    if (window.confirm('Are you sure you want to delete this evaluation?')) {
      deleteEvaluationItem(repository, evaluationId);
    }
  };

  return (
    <Styled.EvaluationCard key={evaluation.id}>
      <Styled.EvaluationBody>
        <EvaluationHeader evaluation={evaluation} repository={repository} />

        <PromptSection evaluation={evaluation} repository={repository} />
        <SourceTargetFiles repository={repository} evaluation={evaluation} />
      </Styled.EvaluationBody>
      <StatusStack evaluation={evaluation} repository={repository} />
    </Styled.EvaluationCard>
  );
};

export default EvaluationCard;
